import { useCallback, useMemo, useState } from 'react';

import { useArchiveMessagingCampaign } from '@occc-app/dataAccessLayer/messagingCenter/mutations';
import { snackbar } from '@occc-app/design-system';

import {
  ARCHIVE_SUCCESS_MESSAGE,
  ARCHIVE_ERROR_MESSAGE,
} from './messaging-campaign-archive.constants';

type ArchiveStatus = 'idle' | 'confirming';
type MessagingCampaignArchiverOptions = {
  onCompleted?: () => void;
};

export const useMessagingCampaignArchiver = (options: MessagingCampaignArchiverOptions = {}) => {
  const { onCompleted } = options;
  const [campaignId, setCampaignId] = useState<string | null>(null);

  const [archiveMessagingCampaign, { loading }] = useArchiveMessagingCampaign();

  const status: ArchiveStatus = useMemo(() => {
    if (campaignId) {
      return 'confirming';
    }

    return 'idle';
  }, [campaignId]);

  const handleConfirm = useCallback(async () => {
    if (!campaignId) {
      return;
    }

    await archiveMessagingCampaign({
      variables: { id: campaignId },
      onCompleted: () => {
        snackbar.success(ARCHIVE_SUCCESS_MESSAGE);
        setCampaignId(null);
        onCompleted?.();
      },
      onError: () => {
        snackbar.error(ARCHIVE_ERROR_MESSAGE);
        setCampaignId(null);
      },
      refetchQueries: ['messagingCampaignsList'],
    });
  }, [archiveMessagingCampaign, campaignId, onCompleted]);

  const handleCancel = useCallback(() => {
    setCampaignId(null);
  }, []);

  const archive = useCallback((campaignId: string) => {
    setCampaignId(campaignId);
  }, []);

  return {
    archive,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
    status,
    isLoading: loading,
    isConfirming: status === 'confirming',
  };
};
